import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI } from '../../services/api';
import { getDisplayStatus, hideImported, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';
import { toast } from 'react-toastify';
import AgentBottomNav from './AgentBottomNav';

const AgentPerformance = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [bills, setBills] = useState([]);
	const [loading, setLoading] = useState(true);

	const fetchData = async () => {
		try {
			setLoading(true);
			// Fetch bills (needed for progress bars, recent activity, and total collected calculation)
			const billsData = await agentsAPI.getAssignedBills(user.id, '');
			setBills(billsData || []);
		} catch (error) {
			toast.error('Failed to load performance data');
			console.error('Error fetching performance data:', error);
			setBills([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (user) {
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const total = bills.length;
	const deliveredBills = bills.filter((p) => p.status === 'delivered');
	const delivered = deliveredBills.length;
	const pending = total - delivered;
	
	// Calculate collected amount from delivered bills (total due amount)
	const collected = deliveredBills.reduce(
		(sum, bill) => sum + (parseFloat(bill.totalDue) || 0),
		0
	);
	
	const efficiency = total > 0 ? Math.round((delivered / total) * 100) : 0;
	
	// Calculate progress percentages (clamp between 0 and 100)
	const pendingPercentage = total > 0 ? Math.min(100, Math.max(0, (pending / total) * 100)) : 0;
	const deliveredPercentage = total > 0 ? Math.min(100, Math.max(0, (delivered / total) * 100)) : 0;

	if (loading) {
		return (
			<div className='h-full flex flex-col'>
				<header className='p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10'>
					<button
						type='button'
						onClick={() => navigate('/agent/dashboard')}
						className='glass-card p-3 rounded-xl'>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<h2 className='text-xl font-black uppercase text-white'>Performance</h2>
				</header>
				<div className='flex-1 flex items-center justify-center'>
					<p className='text-white/60'>Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10'>
				<button
					type='button'
					onClick={() => navigate('/agent/dashboard')}
					className='glass-card p-3 rounded-xl'>
					<ChevronLeft className='w-5 h-5 text-white' />
				</button>
				<h2 className='text-xl font-black uppercase text-white'>Performance</h2>
			</header>

			<div className='flex-1 overflow-y-auto p-6 pb-20 no-scrollbar space-y-6'>
				{/* Performance summary */}
				<div className='grid grid-cols-2 gap-4'>
					<div className='glass-card p-6 text-center'>
						<p className='text-2xl font-black text-[#FFC107]'>{efficiency}%</p>
						<p className='text-sm text-white/60 mt-2'>Efficiency Rate</p>
					</div>
					<div className='glass-card p-6 text-center'>
						<p className='text-2xl font-black text-[#00E676]'>₵{collected.toLocaleString()}</p>
						<p className='text-sm text-white/60 mt-2'>Total collected (delivered bills)</p>
					</div>
				</div>

				{/* Progress Bars */}
				<div className='glass-card p-6'>
					<h3 className='font-bold text-lg mb-4 text-white'>Delivery Progress</h3>
					<div className='space-y-4'>
						<div>
							<div className='flex justify-between mb-1'>
								<span className='text-sm text-white'>Pending Delivery</span>
								<span className='text-sm font-bold text-white'>{pending}</span>
							</div>
							<div className='h-3 bg-white/10 rounded-full overflow-hidden'>
								<div
									className='h-full bg-[#FFC107] transition-all duration-300'
									style={{
										width: `${pendingPercentage}%`,
									}}
								/>
							</div>
						</div>
						<div>
							<div className='flex justify-between mb-1'>
								<span className='text-sm text-white'>Delivered</span>
								<span className='text-sm font-bold text-white'>{delivered}</span>
							</div>
							<div className='h-3 bg-white/10 rounded-full overflow-hidden'>
								<div
									className='h-full bg-[#00E676] transition-all duration-300'
									style={{
										width: `${deliveredPercentage}%`,
									}}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Recent Activity */}
				<div className='glass-card p-6'>
					<h3 className='font-bold text-lg mb-4 text-white'>Recent Activity</h3>
					<div className='space-y-3'>
						{bills.slice(0, 5).map((prop) => (
							<div
								key={getPropertyBillPrimary(prop)}
								className='flex items-center justify-between p-3 bg-white/5 rounded-xl'>
								<div>
									<p className='font-bold text-sm text-white'>
										{getPropertyBillPrimary(prop)}
									</p>
									{getPropertyBillSecondary(prop) && (
										<p className='text-xs text-white/50 font-mono'>
											Valuation: {getPropertyBillSecondary(prop)}
										</p>
									)}
									<p className='text-xs text-white/60'>{hideImported(prop.streetName, '')}</p>
								</div>
								<span className={`status-badge status-${(prop.status && prop.status.toLowerCase() !== 'imported' ? prop.status : 'pending')}`}>
									{getDisplayStatus(prop.status)}
								</span>
							</div>
						))}
						{bills.length === 0 && (
							<p className='text-sm text-white/60 text-center py-4'>
								No activity yet
							</p>
						)}
					</div>
				</div>
			</div>

			{/* Static Footer Navigation */}
			<AgentBottomNav active='performance' />
		</div>
	);
};

export default AgentPerformance;
